import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useChartOfAccounts } from "@/hooks/useChartOfAccounts";
import { TIPOS_PAGAMENTO_FISCAIS } from "@/lib/fiscal-constants";
import type { PaymentMethod, PaymentMethodType } from "@/hooks/usePaymentMethods";

const NO_ACCOUNT_VALUE = "__none__";
const NO_FISCAL_VALUE = "__none__";
const DEFAULT_COLORS = ["#22c55e", "#3b82f6", "#8b5cf6", "#ef4444", "#f97316", "#eab308", "#06b6d4", "#ec4899"];

const formSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório").max(100),
  type: z.enum(["cash", "card", "digital_wallet"]),
  fee_percentage: z.coerce.number().min(0).max(100),
  receiving_days: z.coerce.number().min(0).max(60),
  is_active: z.boolean(),
  chart_account_id: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface PaymentMethodFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentMethod: PaymentMethod | null;
  onSubmit: (data: FormData & { chart_account_id?: string | null; fiscal_code?: string | null; icon_color?: string | null }) => void;
}

const typeLabels: Record<PaymentMethodType, string> = {
  cash: "Dinheiro",
  card: "Cartão",
  digital_wallet: "Carteira Digital",
};

export function PaymentMethodFormDialog({
  open,
  onOpenChange,
  paymentMethod,
  onSubmit,
}: PaymentMethodFormDialogProps) {
  const { accounts, getLeafAccounts, initializeAccounts, isLoading: isLoadingAccounts } = useChartOfAccounts();
  const [chartAccountId, setChartAccountId] = useState<string>(NO_ACCOUNT_VALUE);
  const [fiscalCode, setFiscalCode] = useState<string>(NO_FISCAL_VALUE);
  const [iconColor, setIconColor] = useState<string>("");
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      type: "cash",
      fee_percentage: 0,
      receiving_days: 0,
      is_active: true,
    },
  });

  const selectedType = watch("type");
  const isActive = watch("is_active");

  // Initialize chart of accounts if empty
  useEffect(() => {
    if (open && accounts.length === 0 && !isLoadingAccounts) {
      initializeAccounts();
    }
  }, [open, accounts.length, isLoadingAccounts, initializeAccounts]);

  useEffect(() => {
    if (open) {
      if (paymentMethod) {
        reset({
          name: paymentMethod.name,
          type: paymentMethod.type,
          fee_percentage: paymentMethod.fee_percentage,
          receiving_days: paymentMethod.receiving_days,
          is_active: paymentMethod.is_active,
        });
        setChartAccountId(paymentMethod.chart_account_id || NO_ACCOUNT_VALUE);
        setFiscalCode(paymentMethod.fiscal_code || NO_FISCAL_VALUE);
        setIconColor(paymentMethod.icon_color || "");
      } else {
        reset({
          name: "",
          type: "cash",
          fee_percentage: 0,
          receiving_days: 0,
          is_active: true,
        });
        setChartAccountId(NO_ACCOUNT_VALUE);
        setFiscalCode(NO_FISCAL_VALUE);
        setIconColor("");
      }
    }
  }, [open, paymentMethod, reset]);

  // Get revenue accounts
  const revenueAccounts = getLeafAccounts("revenue");

  const handleFormSubmit = (data: FormData) => {
    onSubmit({
      ...data,
      chart_account_id: chartAccountId === NO_ACCOUNT_VALUE ? null : chartAccountId,
      fiscal_code: fiscalCode === NO_FISCAL_VALUE ? null : fiscalCode,
      icon_color: iconColor || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {paymentMethod ? "Editar Forma de Pagamento" : "Nova Forma de Pagamento"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              placeholder="Ex: Cartão de Crédito"
              {...register("name")}
              disabled={paymentMethod?.is_system_default}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          {/* Type */}
          <div className="space-y-2">
            <Label>Tipo *</Label>
            <Select
              value={selectedType}
              onValueChange={(value: PaymentMethodType) => setValue("type", value)}
              disabled={paymentMethod?.is_system_default}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">💵 Dinheiro</SelectItem>
                <SelectItem value="card">💳 Cartão</SelectItem>
                <SelectItem value="digital_wallet">📱 Carteira Digital</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Taxa de Desconto */}
          <div className="space-y-2">
            <Label htmlFor="fee_percentage">Taxa de Desconto (%)</Label>
            <Input
              id="fee_percentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              placeholder="0.00"
              {...register("fee_percentage")}
            />
            {errors.fee_percentage && (
              <p className="text-sm text-destructive">
                {errors.fee_percentage.message}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Percentual cobrado pela operadora (ex: 2.99 para 2,99%)
            </p>
          </div>

          {/* Prazo de Recebimento */}
          <div className="space-y-2">
            <Label htmlFor="receiving_days">Prazo de Recebimento (dias)</Label>
            <Input
              id="receiving_days"
              type="number"
              step="1"
              min="0"
              max="60"
              placeholder="0"
              {...register("receiving_days")}
            />
            {errors.receiving_days && (
              <p className="text-sm text-destructive">
                {errors.receiving_days.message}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Quantos dias após a venda o valor é creditado (0 = mesmo dia)
            </p>
          </div>

          {/* Fiscal Code for NFC-e */}
          <div className="space-y-2">
            <Label>Código Fiscal (NFC-e)</Label>
            <Select
              value={fiscalCode}
              onValueChange={setFiscalCode}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo fiscal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_FISCAL_VALUE}>(Automático)</SelectItem>
                {TIPOS_PAGAMENTO_FISCAIS.map((tipo) => (
                  <SelectItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Código usado na emissão de NFC-e. Se não definido, será deduzido automaticamente.
            </p>
          </div>

          {/* Chart of Accounts Link */}
          <div className="space-y-2">
            <Label>Conta do Plano de Contas (DRE)</Label>
            <Select
              value={chartAccountId}
              onValueChange={setChartAccountId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma conta (opcional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ACCOUNT_VALUE}>(Sem vínculo)</SelectItem>
                {revenueAccounts.map((acc) => (
                  <SelectItem key={acc.id} value={acc.id}>
                    {acc.code} - {acc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Vincule a uma conta de receita para classificação automática no DRE
            </p>
          </div>

          {/* Cor do Ícone */}
          <div className="space-y-2">
            <Label>Cor do Botão no PDV</Label>
            <div className="flex items-center gap-2 flex-wrap">
              {DEFAULT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setIconColor(color)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${iconColor === color ? "border-foreground scale-110" : "border-transparent"}`}
                  style={{ backgroundColor: color }}
                />
              ))}
              <div className="relative">
                <input
                  type="color"
                  value={iconColor || "#ffffff"}
                  onChange={(e) => setIconColor(e.target.value)}
                  className="w-8 h-8 rounded-full cursor-pointer border border-input bg-transparent p-0"
                  title="Cor personalizada"
                />
              </div>
              {iconColor && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIconColor("")}
                  className="text-xs h-8"
                >
                  Remover cor
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Cor de fundo do botão na tela de recebimento. A cor do texto se ajusta automaticamente.
            </p>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Ativo</Label>
              <p className="text-sm text-muted-foreground">
                Disponível para uso no PDV
              </p>
            </div>
            <Switch
              checked={isActive}
              onCheckedChange={(checked) => setValue("is_active", checked)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
